import { ComponentBase } from "../../core/ComponentBase.js";

export class ChatSearchOverlay extends ComponentBase {
    static styles = `
        .ws-search-overlay {
            position: absolute;
            top: 0;
            right: 0;
            bottom: 0;
            width: 360px;
            background-color: rgba(var(--bs-body-bg-rgb), 0.95);
            backdrop-filter: blur(16px);
            border-left: 1px solid var(--bs-border-color-translucent);
            box-shadow: -4px 0 24px rgba(0,0,0,0.15);
            z-index: 40;
            display: flex;
            flex-direction: column;
            transform: translateX(100%);
            visibility: hidden;
            transition: transform 0.25s cubic-bezier(0.4, 0, 0.2, 1), visibility 0.25s;
        }
        .ws-search-overlay.open {
            transform: translateX(0);
            visibility: visible;
        }

        .ws-search-header {
            padding: 0.85rem 1rem;
            display: flex;
            align-items: center;
            justify-content: space-between;
            border-bottom: 1px solid var(--bs-border-color-translucent);
        }

        .ws-search-title {
            font-size: var(--ab-text-base);
            font-weight: 600;
            margin: 0;
            display: flex;
            align-items: center;
            gap: 0.4rem;
        }

        .ws-search-close-btn {
            background: none;
            border: none;
            color: var(--bs-secondary-color);
            cursor: pointer;
            padding: 0.25rem;
            display: flex;
            align-items: center;
            border-radius: var(--ab-radius-xs);
            transition: background 0.15s, color 0.15s;
        }
        .ws-search-close-btn:hover {
            background-color: var(--bs-tertiary-bg);
            color: var(--bs-body-color);
        }

        .ws-search-input-container {
            padding: 1rem;
            border-bottom: 1px solid var(--bs-border-color-translucent);
            position: relative;
        }

        .ws-search-input-wrapper {
            position: relative;
            display: flex;
            align-items: center;
        }

        .ws-search-input-wrapper i.search-icon {
            position: absolute;
            left: 0.75rem;
            color: var(--bs-secondary-color);
            pointer-events: none;
            font-size: var(--ab-text-base);
        }

        .ws-search-input {
            width: 100%;
            padding: 0.45rem 2.2rem 0.45rem 2rem;
            font-size: var(--ab-text-sm);
            border-radius: var(--ab-radius-xl);
            border: 1px solid var(--bs-border-color);
            background-color: var(--bs-tertiary-bg);
            color: var(--bs-body-color);
            outline: none;
            transition: border-color 0.15s ease, box-shadow 0.15s ease;
        }
        .ws-search-input:focus {
            border-color: var(--bs-primary);
            box-shadow: 0 0 0 0.15rem rgba(var(--bs-primary-rgb), 0.2);
            background-color: var(--bs-body-bg);
        }

        .ws-search-clear-btn {
            position: absolute;
            right: 0.75rem;
            background: none;
            border: none;
            color: var(--bs-secondary-color);
            cursor: pointer;
            padding: 0.1rem;
            font-size: var(--ab-text-sm);
            display: none;
            align-items: center;
        }
        .ws-search-clear-btn:hover {
            color: var(--bs-body-color);
        }

        .ws-search-results {
            flex-grow: 1;
            overflow-y: auto;
            padding: 0.75rem;
        }

        /* Custom scrollbar for search overlay results */
        .ws-search-results::-webkit-scrollbar {
            width: 6px;
        }
        .ws-search-results::-webkit-scrollbar-track {
            background: transparent;
        }
        .ws-search-results::-webkit-scrollbar-thumb {
            background-color: var(--bs-border-color-translucent);
            border-radius: var(--ab-radius-xs);
        }

        .ws-search-loading {
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
            min-height: 120px;
            color: var(--bs-secondary-color);
            gap: 0.5rem;
            font-size: var(--ab-text-sm);
        }

        .ws-search-empty {
            text-align: center;
            padding: 2.5rem 1rem;
            color: var(--bs-secondary-color);
            font-size: var(--ab-text-sm);
        }
        .ws-search-empty i {
            font-size: var(--ab-text-lg);
            margin-bottom: 0.5rem;
            display: block;
            opacity: 0.7;
        }

        .ws-search-result-item {
            display: flex;
            flex-direction: column;
            padding: 0.75rem;
            border-radius: var(--ab-radius-md);
            cursor: pointer;
            margin-bottom: 0.5rem;
            border: 1px solid transparent;
            transition: all 0.15s ease;
        }
        .ws-search-result-item:hover {
            background-color: var(--bs-tertiary-bg);
            border-color: var(--bs-border-color-translucent);
        }

        .ws-search-item-header {
            display: flex;
            align-items: center;
            justify-content: space-between;
            margin-bottom: 0.25rem;
        }

        .ws-search-item-agent {
            font-size: var(--ab-text-xs);
            font-weight: 600;
            color: var(--bs-body-color);
        }

        .ws-search-item-time {
            font-size: var(--ab-text-2xs);
            color: var(--bs-secondary-color);
        }

        .ws-search-item-excerpt {
            font-size: var(--ab-text-sm);
            color: var(--bs-body-color);
            line-height: 1.4;
            display: -webkit-box;
            -webkit-line-clamp: 3;
            -webkit-box-orient: vertical;
            overflow: hidden;
            word-break: break-word;
        }

        .ws-search-item-excerpt mark {
            background-color: rgba(var(--bs-primary-rgb), 0.25);
            color: var(--bs-body-color);
            padding: 0.05rem 0.15rem;
            border-radius: var(--ab-radius-xs);
            font-weight: 500;
        }
    `;

    constructor(config) {
        super(config);
        this.workspace = config.workspace;
        this.chatArea = config.chatArea;
        this.isOpen = false;
        this._searchTimeout = null;
        this.applyStyles();
    }

    async initialize() {
        // Register Escape via the framework HotkeyManager (no local keydown listener needed)
        this.appClient.hotkeys.register('closeSearch', 
            { key: 'Escape' }, 
            'Close search overlay', 
            () => { if (this.isOpen) this.close(); }
        );
        await super.initialize();
    }

    async render(parentContainer) {
        if (!this.container) {
            this.container = document.createElement('div');
            this.container.className = 'ws-search-overlay';

            const header = document.createElement('div');
            header.className = 'ws-search-header';
            header.innerHTML = `
                <h3 class="ws-search-title"><i class="bi bi-search"></i> Search Messages</h3>
                <button class="ws-search-close-btn" title="Close Search"><i class="bi bi-x-lg"></i></button>
            `;
            header.querySelector('.ws-search-close-btn').addEventListener('click', () => this.close());
            this.container.appendChild(header);

            const inputContainer = document.createElement('div');
            inputContainer.className = 'ws-search-input-container';
            inputContainer.innerHTML = `
                <div class="ws-search-input-wrapper">
                    <i class="bi bi-search search-icon"></i>
                    <input type="text" class="ws-search-input" placeholder="Search all messages..." autocomplete="off">
                    <button class="ws-search-clear-btn" title="Clear search"><i class="bi bi-x-circle-fill"></i></button>
                </div>
            `;

            const input = inputContainer.querySelector('.ws-search-input');
            const clearBtn = inputContainer.querySelector('.ws-search-clear-btn');

            input.addEventListener('input', () => {
                const query = input.value.trim();
                clearBtn.style.display = query ? 'flex' : 'none';
                this.debounceSearch(query);
            });

            clearBtn.addEventListener('click', () => {
                input.value = '';
                input.focus();
                clearBtn.style.display = 'none';
                this.debounceSearch('');
            });

            this.container.appendChild(inputContainer);

            this.resultsContainer = document.createElement('div');
            this.resultsContainer.className = 'ws-search-results';
            this.container.appendChild(this.resultsContainer);

            parentContainer.appendChild(this.container);
            this.renderEmptyState("Type something to begin searching.");
        }
        return this.container;
    }

    debounceSearch(query) {
        if (this._searchTimeout) {
            clearTimeout(this._searchTimeout);
        }
        if (!query) {
            this.renderEmptyState("Type something to begin searching.");
            return;
        }
        this._searchTimeout = setTimeout(() => {
            this.executeSearch(query);
        }, 300);
    }

    async executeSearch(query) {
        this.renderLoading();
        try {
            // True cross-thread global message search - do not restrict by threadId
            const res = await this.appClient.rpcClient.call('ChatRpc.searchMessages', {
                query,
                pageSize: 30
            }, { projectId: this.workspace.currentProjectId });

            // RPC returns { data: [], total: N } — not { results: [] }
            const results = res.data || res.results || [];
            if (results.length === 0) {
                this.renderEmptyState('No matching messages found.', 'bi-exclamation-circle');
            } else {
                this.renderResults(results, query);
            }
        } catch (e) {
            this.log.error('Search failed:', e);
            this.renderEmptyState('Search failed. Please try again.');
        }
    }

    renderLoading() {
        this.resultsContainer.innerHTML = `
            <div class="ws-search-loading">
                <div class="spinner-border spinner-border-sm text-primary" role="status"></div>
                <span>Searching...</span>
            </div>
        `;
    }

    renderEmptyState(message, icon = 'bi-search') {
        this.resultsContainer.innerHTML = `
            <div class="ws-search-empty">
                <i class="bi ${icon}"></i>
                <span>${message}</span>
            </div>
        `;
    }

    renderResults(results, query) {
        this.resultsContainer.innerHTML = '';
        if (results.length === 0) {
            this.renderEmptyState("No matching messages found.", 'bi-exclamation-circle');
            return;
        }

        results.forEach(msg => {
            const item = document.createElement('div');
            item.className = 'ws-search-result-item';

            const formattedTime = new Date(msg.createdAt).toLocaleDateString([], {
                month: 'short',
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
            });

            const thread = this.workspace.sidebarComponent?.allThreads.find(t => t._id === msg.threadId);
            const threadName = thread ? (thread.title || 'Untitled Chat') : 'Conversation';
            const roleLabel = msg.role === 'user' ? 'You' : (msg.agentName || this.workspace.currentAgentName || 'Agent');

            item.innerHTML = `
                <div class="ws-search-item-header">
                    <span class="ws-search-item-agent">${this.escapeHtml(roleLabel)} <span class="text-muted fw-normal" style="font-size: var(--ab-text-xs); margin: 0 3px;">in</span> <span class="text-primary">${this.escapeHtml(threadName)}</span></span>
                    <span class="ws-search-item-time">${formattedTime}</span>
                </div>
                <div class="ws-search-item-excerpt"></div>
            `;

            // Excerpt with highlight
            const excerptEl = item.querySelector('.ws-search-item-excerpt');
            excerptEl.innerHTML = this.highlightText(msg.content || '', query);

            item.addEventListener('click', async () => {
                this.workspace._pendingMessageJump = msg._id;
                if (msg.threadId && msg.threadId !== this.workspace.currentThreadId) {
                    await this.workspace.loadThread(msg.threadId);
                } else {
                    await this.chatArea.jumpToMessage(msg._id);
                }
            });

            this.resultsContainer.appendChild(item);
        });
    }

    highlightText(text, query) {
        if (!text) return '';
        // Strip markdown formatting for excerpt
        let cleanText = text
            .replace(/```[\s\S]*?```/g, ' [Code Block] ')
            .replace(/`[^`]+`/g, ' [Code] ')
            .replace(/[#*`\[\]()]/g, ' ')
            .replace(/\s+/g, ' ')
            .trim();

        // Highlight matching query
        const index = cleanText.toLowerCase().indexOf(query.toLowerCase());
        if (index === -1) {
            return this.escapeHtml(cleanText.slice(0, 120));
        }

        const start = Math.max(0, index - 40);
        const end = Math.min(cleanText.length, index + query.length + 80);
        let snippet = cleanText.slice(start, end);
        if (start > 0) snippet = '...' + snippet;
        if (end < cleanText.length) snippet = snippet + '...';

        const escapedSnippet = this.escapeHtml(snippet);
        const regex = new RegExp(`(${this.escapeRegExp(query)})`, 'gi');
        return escapedSnippet.replace(regex, '<mark>$1</mark>');
    }

    escapeRegExp(string) {
        return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    }

    escapeHtml(unsafe) {
        if (!unsafe) return '';
        return unsafe
             .replace(/&/g, "&amp;")
             .replace(/</g, "&lt;")
             .replace(/>/g, "&gt;")
             .replace(/"/g, "&quot;")
             .replace(/'/g, "&#039;");
    }

    open() {
        this.isOpen = true;
        this.container?.classList.add('open');
        // Wait one frame for the visibility transition to complete before focusing
        requestAnimationFrame(() => {
            const input = this.container?.querySelector('.ws-search-input');
            if (input) input.focus();
        });
    }

    close() {
        this.isOpen = false;
        this.container?.classList.remove('open');
        this.workspace.inputComponent?.focus();
    }

    destroy() {
        this.appClient.hotkeys.unregister('closeSearch');
        if (this._searchTimeout) {
            clearTimeout(this._searchTimeout);
        }
        super.destroy();
    }
}
